import classNames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import CustomLineChart from 'shared/components/chart/custom_line_chart/CustomLineChart';
import DashboardSummary from 'shared/components/common/dashboard_summary/DashboardSummary';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import Heading from 'shared/components/common/heading/Heading';
import styles from './style.module.scss';
import CustomBarChart from 'shared/components/chart/custom_bar_chart/CustomBarChart';
import { ProjectNameIcon } from 'assets';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routeConstant } from 'shared/routes/RouteConstant';
import CustomStackBarChart from 'shared/components/chart/custom_stack_bar_chart/CustomStackBarChart';
import GenericTable from 'shared/components/common/table/table';
import { taskEnum } from 'shared/utils/Enum';
import { GetAllUserQuery } from 'shared/services/auth_service/AuthService';
import { GetAllPOQuery } from 'shared/services/po_service/POService';
import { GetAllInventoryQuery } from 'shared/services/inventory_service/InventoryService';
import { GetShopifyProductQuery } from 'shared/services/product_service/ProductService';
import { GetAllTaskQuery } from 'shared/services/task_service/TaskService';
import { GetAllSOQuery } from 'shared/services/so_service/SOService';
import { GetAllITQuery } from 'shared/services/it_service/ITService';
import { GetAllReturnOrderQuery } from 'shared/services/return_service/ReturnService';
import { GetAllRoughListingQuery } from 'shared/services/rough_listing_service/RoughListingService';
import RecentActivity from './RecentActivity';
import moment from 'moment';
import { calculateDifferenceInHours } from 'shared/utils/Helper';
import useCountdown from '../../user/hook/useCountDown';

export default function Dashboard() {
    const navigate = useNavigate();
    const {
        theme: { theme }
    } = useSelector((state) => state.root);

    // Data Queries
    const { data: dataUser } = GetAllUserQuery();
    const { data: dataInventoryList } = GetAllInventoryQuery();
    const { data: dataTask } = GetAllTaskQuery();
    
    const { pendingCount, inprogressCount, completeCount, inProgressList } = React.useMemo(() => {
        const taskList = Array.isArray(dataTask) ? dataTask : [];
        let pending = 0;
        let inprogress = 0;
        let complete = 0;
        const inProgressArr = [];
        taskList.forEach(t => {
            if (t.status === taskEnum.PENDING) pending++;
            else if (t.status === taskEnum.INPROGESS) {
                inprogress++;
                inProgressArr.push(t);
            }
            else if (t.status === taskEnum.COMPLETED) complete++;
        });
        return {
            pendingCount: pending,
            inprogressCount: inprogress,
            completeCount: complete,
            inProgressList: inProgressArr
        };
    }, [dataTask]);
    const { data: dataSaleOrder } = GetAllSOQuery();
    const { data: dataInternalTransfer } = GetAllITQuery();
    const { data: dataReturnOrder } = GetAllReturnOrderQuery();
    const { data: dataPoList, isSuccess } = GetAllPOQuery();
    const { data: shopifyProducts } = GetShopifyProductQuery();
    const { data: dataRoughListing } = GetAllRoughListingQuery();

    // Local State
    const [totalUniqueSkus, setTotalUniqueSkus] = useState(0);
    const [totalQuantity, setTotalQuantity] = useState(0);
    const [warehouseInventory, setWarehouseInventory] = useState([]);

    // Chart State
    const [taskStatusData, setTaskStatusData] = useState({ labels: [], data1: [] });
    const [soTrendData, setSoTrendData] = useState({ labels: [], data1: [] });
    const [chartView, setChartView] = useState('Yearly');
    const [returnTrendData, setReturnTrendData] = useState({ labels: [], data1: [] });
    const [returnChartView, setReturnChartView] = useState('Yearly');

    // Calculate Rough Listing summary counts
    const roughListingSummary = React.useMemo(() => {
        const arr = Array.isArray(dataRoughListing) ? dataRoughListing : dataRoughListing?.data || [];
        
        let totalListings = arr.length;
        let approvedCount = 0;
        let declinedCount = 0;
        let pendingCount = 0;
        let existingCount = 0;

        arr.forEach(rl => {
            (rl.order || []).forEach(item => {
                const status = item.status || 'pending';
                if (status === 'approved') approvedCount++;
                else if (status === 'declined') declinedCount++;
                else if (status === 'existing') existingCount++;
                else if (status === 'pending') pendingCount++;
            });
        });

        return {
            totalListings,
            approvedCount,
            declinedCount,
            pendingCount,
            existingCount
        };
    }, [dataRoughListing]);


    useEffect(() => {
        if (!isSuccess || !dataPoList) return;
        const poArray = Array.isArray(dataPoList) ? dataPoList : Array.isArray(dataPoList.data) ? dataPoList.data : [];
        const allSkus = poArray.flatMap(po => po.order?.map(o => o.sku) || []);
        const uniqueSkus = new Set(allSkus.filter(Boolean));
        setTotalUniqueSkus(uniqueSkus.size);
    }, [dataPoList, isSuccess]);

    useEffect(() => {
        if (!isSuccess || !dataInventoryList) return;
        const inventoryArray = Array.isArray(dataInventoryList) ? dataInventoryList : Array.isArray(dataInventoryList.data) ? dataInventoryList.data : [];

        const warehouseMap = {};
        inventoryArray.forEach((item) => {
            const code = item.warehouse?.code || 'Unknown';
            const qty = Number(item.quantity) || 0;
            warehouseMap[code] = (warehouseMap[code] || 0) + qty;
        });

        const warehouseSummary = Object.entries(warehouseMap).map(([code, qty]) => ({
            title: `Inventory (${code})`,
            count: qty
        }));

        setWarehouseInventory(warehouseSummary);
        const sum = inventoryArray.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
        setTotalQuantity(sum);
    }, [dataInventoryList, isSuccess]);

    // Process Task Data for Charts (Status Breakdown)
    useEffect(() => {
        if (!dataTask || !Array.isArray(dataTask)) return;

        const statusCounts = {
            [taskEnum.PENDING]: 0,
            [taskEnum.INPROGESS]: 0,
            [taskEnum.COMPLETED]: 0
        };

        dataTask.forEach(task => {
            if (statusCounts[task.status] !== undefined) {
                statusCounts[task.status]++;
            }
        });

        setTaskStatusData({
            labels: ['Pending', 'In Progress', 'Completed'],
            data1: [statusCounts[taskEnum.PENDING], statusCounts[taskEnum.INPROGESS], statusCounts[taskEnum.COMPLETED]]
        });
    }, [dataTask]);

    // Build a price map from Sale Orders to look up SKU prices for Return Orders
    const priceMap = React.useMemo(() => {
        const map = {};
        const soArray = Array.isArray(dataSaleOrder) ? dataSaleOrder : dataSaleOrder?.data || [];
        soArray.forEach(so => {
            (so.order || []).forEach(item => {
                if (item.sku && item.price) {
                    map[item.sku] = Number(item.price);
                }
            });
        });
        return map;
    }, [dataSaleOrder]);

    // Process Sale Order Data for Trend Chart
    useEffect(() => {
        const soArray = Array.isArray(dataSaleOrder) ? dataSaleOrder : dataSaleOrder?.data || [];
        if (soArray.length === 0) return;

        const calculateAmount = (order) => {
            return (order.order || []).reduce((acc, item) => {
                const price = Number(item.price) || 0;
                const qty = Number(item.quantity) || 0;
                return acc + (price * qty);
            }, 0);
        };

        if (chartView === 'Yearly') {
            const monthlyAmounts = new Array(12).fill(0);
            const currentYear = new Date().getFullYear();

            soArray.forEach(so => {
                const soDate = new Date(so.created_at || so.createdAt);
                if (soDate.getFullYear() === currentYear) {
                    monthlyAmounts[soDate.getMonth()] += calculateAmount(so);
                }
            });

            setSoTrendData({
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                data1: monthlyAmounts
            });
        } else {
            // Monthly View - breakdown by day
            const now = new Date();
            const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            const dailyAmounts = new Array(daysInMonth).fill(0);
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();

            soArray.forEach(so => {
                const soDate = new Date(so.created_at || so.createdAt);
                if (soDate.getFullYear() === currentYear && soDate.getMonth() === currentMonth) {
                    dailyAmounts[soDate.getDate() - 1] += calculateAmount(so);
                }
            });

            setSoTrendData({
                labels: Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString()),
                data1: dailyAmounts
            });
        }
    }, [dataSaleOrder, chartView]);

    // Process Return Order Data for Trend Chart
    useEffect(() => {
        const returnArray = Array.isArray(dataReturnOrder) ? dataReturnOrder : dataReturnOrder?.data || [];
        if (returnArray.length === 0) return;

        const calculateAmount = (ret) => {
            return (ret.items || []).reduce((acc, item) => {
                const price = priceMap[item.sku] || 0;
                const qty = Number(item.quantity) || 0;
                return acc + (price * qty);
            }, 0);
        };

        if (returnChartView === 'Yearly') {
            const monthlyAmounts = new Array(12).fill(0);
            const currentYear = new Date().getFullYear();

            returnArray.forEach(ret => {
                const retDate = new Date(ret.created_at || ret.createdAt);
                if (retDate.getFullYear() === currentYear) {
                    monthlyAmounts[retDate.getMonth()] += calculateAmount(ret);
                }
            });

            setReturnTrendData({
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                data1: monthlyAmounts
            });
        } else {
            // Monthly View
            const now = new Date();
            const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            const dailyAmounts = new Array(daysInMonth).fill(0);
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();

            returnArray.forEach(ret => {
                const retDate = new Date(ret.created_at || ret.createdAt);
                if (retDate.getFullYear() === currentYear && retDate.getMonth() === currentMonth) {
                    dailyAmounts[retDate.getDate() - 1] += calculateAmount(ret);
                }
            });

            setReturnTrendData({
                labels: Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString()),
                data1: dailyAmounts
            });
        }
    }, [dataReturnOrder, returnChartView, priceMap]);

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Task Name
                    </div>
                ),
                accessor: 'task name',
                Cell: ({ row }) => <div>{row.original?.name}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Assigned User
                    </div>
                ),
                accessor: 'asigned user',
                Cell: ({ row }) => <div>{row.original?.task_assigned?.name}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Priority
                    </div>
                ),
                accessor: 'priority',
                Cell: ({ row }) => <div>{row.original?.priority}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Timer
                    </div>
                ),
                accessor: 'timer',
                Cell: ({ row }) => <TaskTimer task={row.original} />
            }
        ],
        [theme]
    );

    function TaskTimer({ task }) {
        const task_time = React.useMemo(() => {
            const givenDateStr = task?.task_assigned_date;
            if (!givenDateStr) return 0;
            const givenDate = new Date(givenDateStr);
            const currentDate = new Date();
            return calculateDifferenceInHours(currentDate, givenDate);
        }, [task]);

        const { hours, minutes, seconds, isNegative } = useCountdown(task_time);

        return (
            <div className="fw-bold" style={{ color: isNegative ? 'red' : 'green' }}>
                {isNegative ? '-' : ''}
                {hours}h {minutes}m {seconds.toFixed(0)}s
            </div>
        );
    }

    const consolidatedRecentActivity = React.useMemo(() => {
        const prepareList = (list) => {
            const arr = Array.isArray(list) ? list : list?.data || [];
            // Robust sorting by time to ensure we pick the ACTUAL latest for each category
            const getTime = (item) => {
                const dateStr = item.updatedAt || item.createdAt || item.updated_at || item.created_at;
                if (!dateStr) return 0;
                const date = new Date(dateStr);
                return isNaN(date.getTime()) ? 0 : date.getTime();
            };
            return [...arr].sort((a, b) => getTime(b) - getTime(a));
        };

        const getTimeFieldValue = (item) => item.updatedAt || item.createdAt || item.updated_at || item.created_at;

        const getCategoryWinner = (list, type, formatter) => {
            const sorted = prepareList(list);
            if (sorted.length > 0) {
                const item = sorted[0];
                return {
                    ...formatter(item),
                    time: getTimeFieldValue(item)
                };
            }
            return null;
        };

        const winners = [
            getCategoryWinner(dataTask, 'task', item => ({
                id: `task-${item._id}`,
                type: 'task',
                title: item.name,
                subTitle: `Assigned to: ${item.task_assigned?.name || 'Unassigned'}`,
                status: item.status
            })),
            getCategoryWinner(dataPoList, 'po', item => ({
                id: `po-${item._id}`,
                type: 'po',
                title: `PO #${item.order_number}`,
                subTitle: `Supplier: ${item.supplier?.name || '-'}`,
            })),
            getCategoryWinner(dataInventoryList, 'inventory', item => ({
                id: `inv-${item._id}`,
                type: 'inventory',
                title: `SKU: ${item.sku}`,
                subTitle: `Warehouse: ${item.warehouse?.code || '-'}`,
            })),
            getCategoryWinner(dataSaleOrder, 'so', item => ({
                id: `so-${item._id}`,
                type: 'so',
                title: `SO #${item.order_number}`,
                subTitle: `Customer: ${item.shipping?.customerName || '-'}`,
            })),
            getCategoryWinner(dataReturnOrder, 'return', item => ({
                id: `ret-${item._id}`,
                type: 'return',
                title: `Return #${item.order_number}`,
                subTitle: `Warehouse: ${item.warehouse?.code || '-'}`,
            }))
        ].filter(Boolean);

        const getFinalTime = (item) => {
            const date = new Date(item.time);
            return isNaN(date.getTime()) ? 0 : date.getTime();
        };

        return winners.sort((a, b) => getFinalTime(b) - getFinalTime(a));
    }, [dataTask, dataPoList, dataInventoryList, dataSaleOrder, dataReturnOrder]);


    const summaryItems = [
        { title: 'User', count: dataUser?.length, variant: 'VariantPurple', path: routeConstant.admin.manageUser.path },
        { title: 'Total SKUs', count: totalUniqueSkus, variant: 'VariantBlue', path: routeConstant.admin.manageProduct.path },
        { title: 'Purchase Orders', count: dataPoList?.length, variant: 'VariantEmerald', path: routeConstant.admin.po.list.path },
        { title: 'Inventory', count: totalQuantity, variant: 'VariantAmber', path: routeConstant.admin.inventory.list.path },
        ...warehouseInventory.map(wi => ({ ...wi, variant: 'VariantIndigo', path: routeConstant.admin.inventory.list.path })),
        { title: 'Rough Listings', count: roughListingSummary.totalListings, variant: 'VariantBlue', path: routeConstant.admin.roughListing.list.path },
        { title: 'RL Approved Items', count: roughListingSummary.approvedCount, variant: 'VariantEmerald', path: routeConstant.admin.roughListing.list.path },
        { title: 'RL Declined Items', count: roughListingSummary.declinedCount, variant: 'VariantRose', path: routeConstant.admin.roughListing.list.path },
        { title: 'Task', count: dataTask?.length, variant: 'VariantCyan', path: routeConstant.admin.manageTask.path },
        { title: 'Pending Task', count: pendingCount, variant: 'VariantRose', path: routeConstant.admin.manageTask.path },
        { title: 'InProcess Task', count: inprogressCount, variant: 'VariantIndigo', path: routeConstant.admin.manageTask.path },
        { title: 'Completed Task', count: completeCount, variant: 'VariantEmerald', path: routeConstant.admin.manageTask.path },
        { title: 'Sale Order', count: dataSaleOrder?.length, variant: 'VariantBlue', path: routeConstant.admin.so.list.path },
        { title: 'Internal Transfer', count: dataInternalTransfer?.length, variant: 'VariantPurple', path: routeConstant.admin.internalTransfer.list.path },
        { title: 'Return Order', count: dataReturnOrder?.length, variant: 'VariantCyan', path: routeConstant.admin.returnOrder.list.path },
        { title: 'Shopify Products', count: shopifyProducts?.count, variant: 'VariantBlue', path: routeConstant.admin.manageProduct.path }
    ];

    return (
        <DashboardWrapper>
            <div className={classNames(styles.DashboardContainer, 'text-start')}>
                {/* Header Section */}
                <div className={styles.Header}>
                    <Heading heading={'Dashboard Overview'} headingStyle={styles.Heading} />
                    <div className={styles.DateDisplay}>
                        {/* Placeholder for date/filter if needed */}
                    </div>
                </div>

                {/* Stats Grid */}
                <div className={styles.GridContainer}>
                    {summaryItems.map(({ title, count, variant, path }, inx) => (
                        <div key={inx} className={styles.SummaryGridItem}>
                            <DashboardSummary
                                title={title}
                                count={count}
                                variant={variant}
                                path={path}
                                SummaryContainerStyle={styles.SummaryCard}
                            />
                        </div>
                    ))}
                </div>

                {/* Charts Section */}
                <div className={styles.ChartGrid}>
                    <div className={styles.MainChartArea}>
                        <CustomLineChart
                            name={`Sales Amount Trend (${chartView})`}
                            ChartWraper={styles.ChartWraper}
                            labels={soTrendData.labels}
                            data1={soTrendData.data1}
                            currentView={chartView}
                            onViewChange={setChartView}
                        />
                        <div className="mt-4">
                            <CustomLineChart
                                name={`Returns Amount Trend (${returnChartView})`}
                                ChartWraper={styles.ChartWraper}
                                labels={returnTrendData.labels}
                                data1={returnTrendData.data1}
                                currentView={returnChartView}
                                onViewChange={setReturnChartView}
                            />
                        </div>
                    </div>
                    <div className={styles.SideChartArea}>
                        <div className={styles.SmallChart}>
                            <CustomBarChart
                                name={'Task Status Overview'}
                                ChartWraper={styles.ChartWraper}
                                labels={taskStatusData.labels}
                                data1={taskStatusData.data1}
                            />
                        </div>
                        <div className={styles.ActivityContainer}>
                            <RecentActivity data={consolidatedRecentActivity} />
                        </div>
                    </div>
                </div>

                {/* Legacy Stacked Bar Chart - if needed, can serve as another section */}
                {/* <div className="mb-4">
                    <CustomStackBarChart name={'Resources Utilization (Team Based)'} ChartWraper={styles.ChartWraper} />
                </div> */}

                {/* Recent In-Progress Tasks Table */}
                <div className={styles.TableContainer}>
                    <div className={styles.Header}>
                        <Heading heading={'In Progress Tasks'} headingStyle={styles.ProjectHeading} />
                        <div className={styles.ViewAll} onClick={() => navigate(routeConstant.admin.manageTask.path)}>
                            View All
                        </div>
                    </div>
                    <GenericTable
                        columns={columns}
                        data={inProgressList}
                        TDTextStyle={classNames(styles.TDTextStyle)}
                        TableWrapperStyle={classNames(styles.Table)}
                    />
                </div>
            </div>
        </DashboardWrapper>
    );
}

